import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { motion } from 'framer-motion';
import * as workflowService from '../services/workflowService';
import * as exportService from '../services/exportService';
import { getWorkflowTypeGuidance, getWorkflowStepGuidance } from '../data/workflowGuidance';
import useMediaQuery from '../hooks/useMediaQuery';
import { ExportModal } from './Export';
import usePersistedTab from '../hooks/usePersistedTab';

const {
  FiArrowLeft,
  FiPlay,
  FiPause,
  FiCheck,
  FiClock,
  FiUser,
  FiCalendar,
  FiEdit3,
  FiPlus,
  FiSave,
  FiX,
  FiCheckCircle,
  FiAlertTriangle,
  FiMessageSquare,
  FiFileText,
  FiLightbulb,
  FiChevronDown,
  FiChevronUp,
  FiInfo,
  FiShield,
  FiDownload
} = FiIcons;

const WorkflowDetails = () => {
  const { workflowId } = useParams();
  const [workflow, setWorkflow] = useState(null);
  const [activeTab, setActiveTab] = usePersistedTab('guidance');
  const [editingStep, setEditingStep] = useState(null);
  const [showNoteForm, setShowNoteForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedStepGuidance, setExpandedStepGuidance] = useState({});
  const isMobile = useMediaQuery('(max-width: 767px)');
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  useEffect(() => {
    loadWorkflowData();
  }, [workflowId]);

  const loadWorkflowData = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await workflowService.getWorkflowById(workflowId);
      setWorkflow(data);
    } catch (err) {
      console.error('Error loading workflow:', err);
      setError('Failed to load workflow details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (exportType, options) => {
    try {
      setExportLoading(true);
      await exportService.exportDocument('workflow', workflowId, exportType, options);
    } catch (err) {
      console.error('Error exporting workflow:', err);
      throw err;
    } finally {
      setExportLoading(false);
    }
  };

  const updateStepStatus = async (stepId, newStatus, assignedTo = '', notes = '') => {
    try {
      setLoading(true);
      setError(null);

      // Update the step
      await workflowService.updateWorkflowStep(stepId, {
        status: newStatus,
        assigned_to: assignedTo,
        notes: notes
      });

      // Add audit entry
      const stepName = workflow.workflow_steps.find(s => s.id === stepId)?.step_name || 'Unknown step';
      await workflowService.addWorkflowAuditEntry(
        workflow.id,
        'Step Updated',
        `Updated step "${stepName}" to ${newStatus}${assignedTo ? ` (assigned to ${assignedTo})` : ''}`
      );

      // Reload workflow data
      await loadWorkflowData();
    } catch (err) {
      console.error('Error updating step:', err);
      // Extract user-friendly error message
      let errorMessage = 'Failed to update step. Please try again.';
      if (err instanceof Error) {
        errorMessage = err.message;
      } else if (typeof err === 'string') {
        errorMessage = err;
      }
      setError(errorMessage);
    } finally {
      // Always reset loading state to stop spinner
      setLoading(false);
    }
  };

  const getStepStatusIcon = (status) => {
    switch (status) {
      case 'completed': return FiCheckCircle;
      case 'active': return FiPlay;
      case 'ready': return FiClock;
      case 'pending': return FiClock;
      default: return FiClock;
    }
  };

  const getStepStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-50 border-green-200';
      case 'active': return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'ready': return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'pending': return 'text-gray-600 bg-gray-50 border-gray-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const canEditStep = (step, stepIndex) => {
    // Can always edit pending, ready, active, and completed steps
    return true;
  };

  const getAvailableStatuses = (step, stepIndex) => {
    const allStatuses = [
      { value: 'pending', label: 'Pending' },
      { value: 'ready', label: 'Ready' },
      { value: 'active', label: 'Active' },
      { value: 'completed', label: 'Completed' }
    ];

    // For the first step, allow all statuses
    if (stepIndex === 0) {
      return allStatuses;
    }

    // For subsequent steps, check if previous step is completed
    const previousStep = workflow.workflow_steps[stepIndex - 1];
    const isPreviousCompleted = previousStep?.status === 'completed';

    if (!isPreviousCompleted) {
      // If previous step isn't completed, only allow pending and ready
      return allStatuses.filter(status => ['pending', 'ready'].includes(status.value));
    }

    // If previous step is completed, allow all statuses
    return allStatuses;
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="text-center py-12">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600 font-medium">Loading workflow details...</p>
        </div>
      </div>
    );
  }

  if (error && !workflow) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <SafeIcon icon={FiAlertTriangle} className="text-red-600 text-4xl mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-red-900 mb-2">Error Loading Workflow</h3>
          <p className="text-red-700 mb-4">{error}</p>
          <Link to="/company-workflows" className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700">
            Back to Workflows
          </Link>
        </div>
      </div>
    );
  }

  if (!workflow) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="text-center py-12">
          <SafeIcon icon={FiAlertTriangle} className="text-6xl text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Workflow Not Found</h3>
          <p className="text-gray-600 mb-4">The requested workflow could not be found.</p>
          <Link to="/company-workflows" className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700">
            Back to Workflows
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <Link to="/company-workflows" className="mr-4">
            <SafeIcon icon={FiArrowLeft} className="text-2xl text-gray-600 hover:text-gray-900" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{workflow.workflow_name}</h1>
            <p className="text-gray-600">{workflow.workflow_id} • {workflow.department}</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <span
            className={`px-3 py-1 rounded-full text-sm font-medium ${workflow.status === 'active'
              ? 'bg-blue-100 text-blue-800'
              : workflow.status === 'completed'
                ? 'bg-green-100 text-green-800'
                : workflow.status === 'planning'
                  ? 'bg-yellow-100 text-yellow-800'
                  : 'bg-gray-100 text-gray-800'
              }`}
          >
            {workflow.status.toUpperCase()}
          </span>
          <button
            onClick={() => setShowExportModal(true)}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center space-x-2"
          >
            <SafeIcon icon={FiDownload} />
            <span>Export</span>
          </button>
        </div>
      </div>

      {/* Workflow Summary */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="grid md:grid-cols-4 gap-6">
          <div>
            <h3 className="text-sm font-medium text-gray-500">Owner</h3>
            <p className="mt-1 text-lg font-semibold">{workflow.owner}</p>
            <p className="text-sm text-gray-600">{workflow.department}</p>
          </div>
          <div>
            <h3 className="text-sm font-medium text-gray-500">Progress</h3>
            <p className="mt-1 text-lg font-semibold">{workflow.progress.completionPercentage}%</p>
            <p className="text-sm text-gray-600">
              {workflow.progress.completedSteps}/{workflow.progress.totalSteps} steps
            </p>
          </div>
          <div>
            <h3 className="text-sm font-medium text-gray-500">Timeline</h3>
            <p className="mt-1 text-sm text-gray-900">
              Start: {new Date(workflow.start_date).toLocaleDateString()}
            </p>
            <p className="text-sm text-gray-600">
              Est. End: {new Date(workflow.estimated_end_date).toLocaleDateString()}
            </p>
          </div>
          <div>
            <h3 className="text-sm font-medium text-gray-500">Priority</h3>
            <span
              className={`mt-1 inline-block px-2 py-1 rounded text-sm font-medium ${workflow.priority === 'high'
                ? 'bg-red-100 text-red-800'
                : workflow.priority === 'medium'
                  ? 'bg-yellow-100 text-yellow-800'
                  : 'bg-green-100 text-green-800'
                }`}
            >
              {workflow.priority?.toUpperCase()}
            </span>
          </div>
        </div>

        {/* Enhanced Progress Bar */}
        <div className="mt-6">
          <div className="flex justify-between text-sm mb-2">
            <span className="font-medium text-gray-700">Overall Progress</span>
            <span className="text-gray-600">{workflow.progress.completionPercentage}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
            <div
              className="bg-gradient-to-r from-blue-500 to-green-500 h-3 rounded-full transition-all duration-500"
              style={{ width: `${workflow.progress.completionPercentage}%` }}
            />
          </div>
          <div className="flex justify-between text-xs text-gray-500">
            <span>{workflow.progress.completedSteps} completed</span>
            <span>{workflow.progress.totalSteps - workflow.progress.completedSteps} remaining</span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="border-b border-gray-200">
          {isMobile ? (
            <div className="p-4">
              <label htmlFor="workflow-tabs" className="sr-only">Select a tab</label>
              <div className="relative">
                <select
                  id="workflow-tabs"
                  value={activeTab}
                  onChange={(e) => setActiveTab(e.target.value)}
                  className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md appearance-none bg-white border"
                >
                  {[
                    { id: 'guidance', name: 'Workflow Guidance' },
                    { id: 'steps', name: 'Workflow Steps' },
                    { id: 'timeline', name: 'Timeline' },
                    { id: 'notes', name: 'Notes' },
                    { id: 'audit', name: 'Audit Log' }
                  ].map((tab) => (
                    <option key={tab.id} value={tab.id}>
                      {tab.name}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                  <SafeIcon icon={FiChevronDown} className="h-4 w-4" />
                </div>
              </div>
            </div>
          ) : (
            <nav className="flex space-x-8 px-6">
              {[
                { id: 'guidance', name: 'Workflow Guidance', icon: FiLightbulb },
                { id: 'steps', name: 'Workflow Steps', icon: FiCheckCircle },
                { id: 'timeline', name: 'Timeline', icon: FiCalendar },
                { id: 'notes', name: 'Notes', icon: FiMessageSquare },
                { id: 'audit', name: 'Audit Log', icon: FiClock }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                >
                  <SafeIcon icon={tab.icon} />
                  <span>{tab.name}</span>
                </button>
              ))}
            </nav>
          )}
        </div>

        <div className="p-6">
          {/* Guidance Tab */}
          {activeTab === 'guidance' && (() => {
            const workflowGuidance = getWorkflowTypeGuidance(workflow.workflow_type);
            if (!workflowGuidance) {
              return (
                <div className="text-center py-8 text-gray-500">
                  <SafeIcon icon={FiInfo} className="text-4xl mx-auto mb-2" />
                  <p>No guidance available for this workflow type.</p>
                </div>
              );
            }

            return (
              <div className="border-2 border-purple-200 rounded-lg bg-gradient-to-r from-purple-50 to-pink-50 p-6">
                <div className="flex items-center space-x-2 mb-4">
                  <SafeIcon icon={FiLightbulb} className="text-purple-600 text-2xl" />
                  <h2 className="text-xl font-bold text-purple-900">Workflow Guidance</h2>
                </div>

                {/* Importance */}
                <div className="mb-4 p-4 bg-purple-100 bg-opacity-50 rounded-lg">
                  <div className="flex items-start space-x-2">
                    <SafeIcon icon={FiInfo} className="text-purple-700 mt-1 flex-shrink-0" />
                    <div>
                      <h3 className="font-semibold text-purple-900 mb-1">Why This Matters</h3>
                      <p className="text-sm text-purple-800">{workflowGuidance.importance}</p>
                    </div>
                  </div>
                </div>

                {/* What to Look For */}
                <div className="mb-4 p-4 bg-blue-100 bg-opacity-50 rounded-lg">
                  <div className="flex items-start space-x-2">
                    <SafeIcon icon={FiCheckCircle} className="text-blue-700 mt-1 flex-shrink-0" />
                    <div className="flex-1">
                      <h3 className="font-semibold text-blue-900 mb-2">What to Look For</h3>
                      <ul className="list-disc list-inside space-y-1 text-sm text-blue-800">
                        {workflowGuidance.whatToLookFor.map((item, i) => (
                          <li key={i}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Best Practices */}
                <div className="mb-4 p-4 bg-green-100 bg-opacity-50 rounded-lg">
                  <div className="flex items-start space-x-2">
                    <SafeIcon icon={FiCheck} className="text-green-700 mt-1 flex-shrink-0" />
                    <div className="flex-1">
                      <h3 className="font-semibold text-green-900 mb-2">Best Practices</h3>
                      <ul className="list-disc list-inside space-y-1 text-sm text-green-800">
                        {workflowGuidance.bestPractices.map((item, i) => (
                          <li key={i}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Common Pitfalls */}
                <div className="mb-4 p-4 bg-amber-100 bg-opacity-50 rounded-lg">
                  <div className="flex items-start space-x-2">
                    <SafeIcon icon={FiAlertTriangle} className="text-amber-700 mt-1 flex-shrink-0" />
                    <div className="flex-1">
                      <h3 className="font-semibold text-amber-900 mb-2">Common Pitfalls to Avoid</h3>
                      <ul className="list-disc list-inside space-y-1 text-sm text-amber-800">
                        {workflowGuidance.commonPitfalls.map((item, i) => (
                          <li key={i}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Legal Considerations */}
                <div className="p-4 bg-red-100 bg-opacity-50 rounded-lg">
                  <div className="flex items-start space-x-2">
                    <SafeIcon icon={FiShield} className="text-red-700 mt-1 flex-shrink-0" />
                    <div className="flex-1">
                      <h3 className="font-semibold text-red-900 mb-2">Legal Considerations</h3>
                      <ul className="list-disc list-inside space-y-1 text-sm text-red-800">
                        {workflowGuidance.legalConsiderations.map((item, i) => (
                          <li key={i}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Steps Tab */}
          {activeTab === 'steps' && (
            <div className="space-y-4">
              {workflow.workflow_steps.map((step, index) => (
                <motion.div
                  key={step.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={`border rounded-lg p-4 ${getStepStatusColor(step.status)}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <SafeIcon icon={getStepStatusIcon(step.status)} className="text-xl" />
                      <div>
                        <h4 className="font-medium">{step.step_name}</h4>
                        <p className="text-sm opacity-75">
                          Duration: {step.duration_days} days
                          {step.is_critical && <span className="ml-2 text-red-600 font-bold">• Critical</span>}
                          <span className="ml-2 text-gray-500">
                            Step {index + 1} of {workflow.workflow_steps.length}
                          </span>
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span
                        className={`px-2 py-1 rounded text-xs font-medium capitalize ${getStepStatusColor(
                          step.status
                        )}`}
                      >
                        {step.status}
                      </span>
                      {canEditStep(step, index) && (
                        <button
                          onClick={() => setEditingStep({ ...step, stepIndex: index })}
                          className="p-1 hover:bg-white hover:bg-opacity-50 rounded"
                        >
                          <SafeIcon icon={FiEdit3} className="text-sm" />
                        </button>
                      )}
                    </div>
                  </div>

                  {step.assigned_to && (
                    <div className="mt-2 flex items-center space-x-2 text-sm">
                      <SafeIcon icon={FiUser} />
                      <span>Assigned to: {step.assigned_to}</span>
                    </div>
                  )}

                  {step.notes && (
                    <div className="mt-2 p-2 bg-white bg-opacity-50 rounded text-sm">
                      <strong>Notes:</strong> {step.notes}
                    </div>
                  )}

                  {(step.started_at || step.completed_at) && (
                    <div className="mt-2 text-xs opacity-75">
                      {step.started_at && <span>Started: {new Date(step.started_at).toLocaleString()}</span>}
                      {step.completed_at && (
                        <span className="ml-4">Completed: {new Date(step.completed_at).toLocaleString()}</span>
                      )}
                    </div>
                  )}

                  {/* Step Guidance */}
                  {(() => {
                    const stepGuidance = getWorkflowStepGuidance(workflow.workflow_type, step.step_name);
                    if (!stepGuidance) return null;

                    const isExpanded = expandedStepGuidance[step.id] || false;

                    return (
                      <div className="mt-3 border-2 border-blue-200 rounded-lg bg-gradient-to-r from-blue-50 to-indigo-50 overflow-hidden">
                        <button
                          onClick={() => setExpandedStepGuidance(prev => ({ ...prev, [step.id]: !prev[step.id] }))}
                          className="w-full px-4 py-3 flex items-center justify-between hover:bg-blue-100 hover:bg-opacity-50 transition-colors"
                        >
                          <div className="flex items-center space-x-2">
                            <SafeIcon icon={FiLightbulb} className="text-blue-600 text-lg" />
                            <span className="font-semibold text-blue-900">Step Guidance</span>
                          </div>
                          <SafeIcon
                            icon={isExpanded ? FiChevronUp : FiChevronDown}
                            className="text-blue-600"
                          />
                        </button>

                        {isExpanded && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            className="px-4 pb-4"
                          >
                            {/* Main Guidance */}
                            <div className="prose prose-sm max-w-none text-gray-700 mb-3">
                              {stepGuidance.guidance.split('\n').map((line, i) => (
                                <p key={i} className="mb-2 whitespace-pre-wrap">{line}</p>
                              ))}
                            </div>

                            {/* Tips */}
                            {stepGuidance.tips && stepGuidance.tips.length > 0 && (
                              <div className="mb-3 p-3 bg-green-50 border border-green-200 rounded">
                                <div className="flex items-start space-x-2">
                                  <SafeIcon icon={FiInfo} className="text-green-600 mt-0.5 flex-shrink-0" />
                                  <div className="flex-1">
                                    <h4 className="font-semibold text-green-900 mb-1">Tips:</h4>
                                    <ul className="list-disc list-inside space-y-1 text-sm text-green-800">
                                      {stepGuidance.tips.map((tip, i) => (
                                        <li key={i}>{tip}</li>
                                      ))}
                                    </ul>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Warnings */}
                            {stepGuidance.warnings && stepGuidance.warnings.length > 0 && (
                              <div className="p-3 bg-amber-50 border border-amber-200 rounded">
                                <div className="flex items-start space-x-2">
                                  <SafeIcon icon={FiAlertTriangle} className="text-amber-600 mt-0.5 flex-shrink-0" />
                                  <div className="flex-1">
                                    <h4 className="font-semibold text-amber-900 mb-1">Important Warnings:</h4>
                                    <ul className="list-disc list-inside space-y-1 text-sm text-amber-800">
                                      {stepGuidance.warnings.map((warning, i) => (
                                        <li key={i}>{warning}</li>
                                      ))}
                                    </ul>
                                  </div>
                                </div>
                              </div>
                            )}
                          </motion.div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Step Dependencies */}
                  {index > 0 &&
                    workflow.workflow_steps[index - 1].status !== 'completed' &&
                    step.status !== 'pending' && (
                      <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800">
                        <SafeIcon icon={FiAlertTriangle} className="inline mr-1" />
                        <strong>Dependency:</strong> Previous step "{workflow.workflow_steps[index - 1].step_name}" must
                        be completed first
                      </div>
                    )}
                </motion.div>
              ))}
            </div>
          )}

          {/* Timeline Tab */}
          {activeTab === 'timeline' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold mb-4">Workflow Timeline</h3>
              <div className="relative">
                {workflow.workflow_steps.map((step, index) => (
                  <div key={step.id} className="flex items-start space-x-4 pb-6 relative">
                    {/* Timeline line */}
                    {index < workflow.workflow_steps.length - 1 && (
                      <div className="absolute left-4 top-8 w-px h-6 bg-gray-300"></div>
                    )}

                    <div
                      className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${step.status === 'completed'
                        ? 'bg-green-500'
                        : step.status === 'active'
                          ? 'bg-blue-500'
                          : step.status === 'ready'
                            ? 'bg-orange-500'
                            : 'bg-gray-300'
                        }`}
                    >
                      <SafeIcon icon={getStepStatusIcon(step.status)} className="text-white text-sm" />
                    </div>

                    <div className="flex-1">
                      <h4 className="font-medium">{step.step_name}</h4>
                      <p className="text-sm text-gray-600">
                        Duration: {step.duration_days} days
                        {step.is_critical && <span className="ml-2 text-red-600 font-bold">• Critical</span>}
                      </p>
                      {step.assigned_to && <p className="text-sm text-gray-500">Assigned to: {step.assigned_to}</p>}
                      {step.started_at && (
                        <p className="text-xs text-gray-500">Started: {new Date(step.started_at).toLocaleDateString()}</p>
                      )}
                      {step.completed_at && (
                        <p className="text-xs text-gray-500">
                          Completed: {new Date(step.completed_at).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Notes Tab */}
          {activeTab === 'notes' && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Workflow Notes</h3>
                <button
                  onClick={() => setShowNoteForm(true)}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center space-x-2"
                >
                  <SafeIcon icon={FiPlus} />
                  <span>Add Note</span>
                </button>
              </div>

              <div className="space-y-4">
                {(workflow.workflow_notes || []).map(note => (
                  <div key={note.id} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="font-medium text-gray-900">{note.note_title}</h4>
                      <span className="text-xs text-gray-500">{new Date(note.created_at).toLocaleString()}</span>
                    </div>
                    <p className="text-gray-700">{note.note_content}</p>
                    <div className="mt-2 text-xs text-gray-500">By: {note.created_by}</div>
                  </div>
                ))}

                {(!workflow.workflow_notes || workflow.workflow_notes.length === 0) && (
                  <div className="text-center py-8 text-gray-500">
                    No notes added yet. Click "Add Note" to get started.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Audit Log Tab */}
          {activeTab === 'audit' && (
            <div>
              <h3 className="text-lg font-semibold mb-4">Audit Log</h3>
              <div className="space-y-4">
                {(workflow.workflow_audit_log || []).map(entry => (
                  <div key={entry.id} className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg">
                    <div className="flex-shrink-0 w-2 h-2 bg-blue-600 rounded-full mt-2"></div>
                    <div className="flex-1">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-medium text-gray-900">{entry.action}</h4>
                          <p className="text-gray-700 mt-1">{entry.details}</p>
                        </div>
                        <div className="text-right text-sm text-gray-500">
                          <p>{new Date(entry.performed_at).toLocaleString()}</p>
                          <p>By: {entry.performed_by}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {(!workflow.workflow_audit_log || workflow.workflow_audit_log.length === 0) && (
                  <div className="text-center py-8 text-gray-500">No audit entries yet.</div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Edit Step Modal */}
      {editingStep && (
        <StepEditModal
          step={editingStep}
          availableStatuses={getAvailableStatuses(editingStep, editingStep.stepIndex)}
          onSubmit={updatedData => {
            updateStepStatus(editingStep.id, updatedData.status, updatedData.assignedTo, updatedData.notes);
            setEditingStep(null);
          }}
          onCancel={() => setEditingStep(null)}
        />
      )}

      {/* Note Form Modal */}
      {showNoteForm && (
        <NoteFormModal
          onSubmit={async noteData => {
            try {
              await workflowService.addWorkflowNote(workflow.id, {
                title: noteData.title,
                content: noteData.content
              });

              await loadWorkflowData();
              setShowNoteForm(false);
            } catch (err) {
              console.error('Error adding note:', err);
              setError('Failed to add note. Please try again.');
            }
          }}
          onCancel={() => setShowNoteForm(false)}
        />
      )}

      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExport={handleExport}
        entityType="workflow"
        entityName={`${workflow.workflow_id} - ${workflow.workflow_name}`}
        isLoading={exportLoading}
      />
    </div>
  );
};

// Enhanced Step Edit Modal Component
const StepEditModal = ({ step, availableStatuses, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    status: step.status,
    assignedTo: step.assigned_to || '',
    notes: step.notes || ''
  });

  const handleSubmit = e => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
      >
        <h2 className="text-xl font-bold text-gray-900 mb-4">Edit Step: {step.step_name}</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={formData.status}
              onChange={e => setFormData(prev => ({ ...prev, status: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              {availableStatuses.map(status => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
            {step.stepIndex > 0 && !availableStatuses.find(s => s.value === 'active') && (
              <p className="text-xs text-yellow-600 mt-1">
                Previous step must be completed before this step can be activated
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Assigned To</label>
            <input
              type="text"
              value={formData.assignedTo}
              onChange={e => setFormData(prev => ({ ...prev, assignedTo: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Enter assignee name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={e => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              rows="3"
              placeholder="Add notes about this step"
            />
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button type="submit" className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              Save Changes
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

// Note Form Modal Component
const NoteFormModal = ({ onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    title: '',
    content: ''
  });

  const handleSubmit = e => {
    e.preventDefault();
    if (formData.title && formData.content) {
      onSubmit(formData);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6"
      >
        <h2 className="text-xl font-bold text-gray-900 mb-4">Add Workflow Note</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input
              type="text"
              value={formData.title}
              onChange={e => setFormData(prev => ({ ...prev, title: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Note title"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
            <textarea
              value={formData.content}
              onChange={e => setFormData(prev => ({ ...prev, content: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              rows="4"
              placeholder="Note content"
              required
            />
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button type="submit" className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              Add Note
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default WorkflowDetails;